import React, { FC, MutableRefObject, ReactElement, useContext, useRef, useState } from 'react'
import { ReactState } from '../../Common/Types/Types'
import { Availability, getEnabledAvailabilities, MAX_SEARCH_QUERY_LENGTH, SortingOptions } from '../Constant/Constants'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import { Presence } from '../../../../i18n'
import TextInput from '../../Common/Components/TextInput'
import { PresenceFilters } from '../Types/Types'
import { ButtonGroup } from '../../Common/Components/ButtonGroup/ButtonGroup'
import { ButtonGroupEntry } from '../../Common/Components/ButtonGroup/Types'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { Select } from '../../Common/Components/Selects'
import Icon, { IconName } from '../../Common/Components/Icon'
import { DarkModeType, useDarkMode } from '../../Common/Hooks/useDarkMode'
import PopperContent from '../../Common/Components/PopperContent'
import { PresenceMoreFilters } from './PresenceMoreFilters'
import { DevicesResponse } from '../../../NonReact/Types/Types'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'

type Props = {
    isDarkModeSwitch?: boolean
    sortModeState: ReactState<SortingOptions>
    filterState: ReactState<PresenceFilters>
    searchState: ReactState<string>
    countsByAvailability: { [key in Availability]: number }
    departments: DevicesResponse[]
    queues: DevicesResponse[]
}

export const PresenceHeader: FC<Props> = ({
    isDarkModeSwitch,
    sortModeState: [sortMode, setSortMode],
    filterState,
    searchState: [search, setSearch],
    countsByAvailability,
    departments,
    queues
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(Presence.NAME)

    const enableOutOfOffice: Definable<boolean> = useFeatureFlag('enableOutOfOfficePresence')

    const { darkModeSwitch }: DarkModeType = useDarkMode()

    const [filters, setFilters]: ReactState<PresenceFilters> = filterState

    const [isFiltersOpen, setIsFiltersOpen]: ReactState<boolean> = useState<boolean>(false)

    const filtersRef: MutableRefObject<null> = useRef<null>(null)

    return (
        <>
            <div className='presence-header'>
                <div className='ph-header-top'>
                    <h1 className='ph-page-title'>{language.get(Presence.Presence_Page_Title)}</h1>
                    <div className='ph-right-container'>
                        {isDarkModeSwitch ? darkModeSwitch : <></>}
                        <div className='ph-search'>
                            <TextInput
                                value={search}
                                maxLength={MAX_SEARCH_QUERY_LENGTH}
                                onChange={setSearch}
                                variant='outlined'
                                labelInput={language.get(Presence.Presence_Search_Title)}
                            />
                        </div>
                        <div className='ph-sorting-container'>
                            <Select
                                options={[
                                    {
                                        label: language.get(Presence.Presence_SortBy_Options_FullName_ASC),
                                        value: SortingOptions.ASC
                                    },
                                    {
                                        label: language.get(Presence.Presence_SortBy_Options_FullName_DSC),
                                        value: SortingOptions.DESC
                                    }
                                ]}
                                value={sortMode}
                                handleChange={setSortMode}
                                label={language.get(Presence.Presence_SortBy_Title)}
                                labelClass='ph-sorting-title'
                            />
                        </div>
                    </div>
                </div>
                {isVarNotEmpty(countsByAvailability) ? (
                    <div className='ph-header-bottom'>
                        <ButtonGroup
                            name='tp-presence-users'
                            entries={[
                                {
                                    isActive: (filters?.availability?.length ?? 0) === 0,
                                    onClick: (): void =>
                                        setFilters(
                                            (previousFilters: PresenceFilters): PresenceFilters => ({
                                                ...previousFilters,
                                                availability: []
                                            })
                                        ),
                                    content: (
                                        <span className='tp-filter-button'>
                                            {language.get(Presence.Presence_Filter_Availability_All)}
                                        </span>
                                    )
                                },
                                ...getEnabledAvailabilities(!!enableOutOfOffice).map(
                                    (availability: Availability): ButtonGroupEntry => ({
                                        buttonClassName: `tp-presence-filter tp-presence-filter-${availability.toLowerCase()}`,
                                        isActive: !!filters?.availability?.includes(availability),
                                        onClick: (): void =>
                                            setFilters(
                                                (previousFilters: PresenceFilters): PresenceFilters => ({
                                                    ...previousFilters,
                                                    availability: previousFilters?.availability?.includes(availability)
                                                        ? ArrayUtils.removeFromArray<Availability>(
                                                              previousFilters?.availability ?? [],
                                                              availability
                                                          )
                                                        : [...(previousFilters?.availability ?? []), availability]
                                                })
                                            ),
                                        content: (
                                            <>
                                                <span className='tp-filter-button'>
                                                    {language.get(`Presence_Filter_Availability_${availability}`)}
                                                </span>
                                                <span className='tp-filter-count'>
                                                    ({countsByAvailability?.[availability] ?? 0})
                                                </span>
                                            </>
                                        )
                                    })
                                )
                            ]}
                        />
                        {departments?.length > 0 || queues?.length > 0 ? (
                            <>
                                <ButtonGroup
                                    name='tp-presence-filter'
                                    className='tp-presence-filter-group'
                                    entries={[
                                        {
                                            buttonRef: filtersRef,
                                            content: (
                                                <>
                                                    <span className='pf-icon'>
                                                        <Icon iconName={IconName.FILTER} />
                                                    </span>
                                                    <span className='pf-text'>
                                                        {language.get(Presence.Presence_Filter_Title)}
                                                    </span>
                                                </>
                                            ),
                                            onClick: (): void =>
                                                setIsFiltersOpen(
                                                    (previousIsFiltersOpen: boolean): boolean => !previousIsFiltersOpen
                                                )
                                        }
                                    ]}
                                />
                                <PopperContent
                                    className='ph-filters-popper'
                                    anchorEl={filtersRef.current}
                                    open={isFiltersOpen}
                                    setOpen={setIsFiltersOpen}
                                    isClickAwayHandled
                                >
                                    <PresenceMoreFilters
                                        departments={departments}
                                        queues={queues}
                                        filtersState={filterState}
                                    />
                                </PopperContent>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                ) : (
                    <></>
                )}
            </div>
        </>
    )
}
